import {
  DataGridBox,
  DeleteDialog,
  CustomToolbar,
  ZebraDataGrid,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import { useSnackbar } from 'notistack';
import { STORAGE_KEY_ENUM } from 'enums';
import apiService from 'library/api-service';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import { renderDataGridLocaleText } from 'utils';
import {
  selectConnectionTypes,
  selectConnectionTypesIsEmpty,
  selectConnectionTypesLoading,
} from 'store/features/connection-types/selector';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Grid2 as Grid } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { selectPermissionCodes } from 'store/features/permissions/selector';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import connectionTypesActions from 'store/features/connection-types/actions';
import { CreateConnectionTypeDialog, UpdateConnectionTypeDialog } from './components';

const ConnectionTypes = () => {
  // other hooks
  const intl = useIntl();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // redux hooks
  const dispatch = useAppDispatch();
  const loading = useAppSelector(selectConnectionTypesLoading);
  const permissionCodes = useAppSelector(selectPermissionCodes);
  const connectionTypes = useAppSelector(selectConnectionTypes);
  const isEmptyConnectionTypes = useAppSelector(selectConnectionTypesIsEmpty);

  // useState hooks
  const [openDeleteConnectionType, setOpenDeleteConnectionType] = useState(false);
  const [openCreateConnectionType, setOpenCreateConnectionType] = useState(false);
  const [openUpdateConnectionType, setOpenUpdateConnectionType] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: intl.formatMessage({ id: 'connectionTypes.title', defaultMessage: 'Adı' }),
        flex: 1,
      },
    ],
    [intl]
  );

  // async helper functions
  const refresh = async () => {
    dispatch(connectionTypesActions.getConnectionTypes());
  };

  // sync helper functions
  const handleCloseDeleteConnectionType = () => setOpenDeleteConnectionType(false);
  const handleClickOpenDeleteConnectionType = () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenDeleteConnectionType(true);
  };

  const handleCloseCreateConnectionType = () => setOpenCreateConnectionType(false);
  const handleClickOpenCreateConnectionType = () => setOpenCreateConnectionType(true);

  const handleCloseUpdateConnectionType = () => setOpenUpdateConnectionType(false);
  const handleClickOpenUpdateConnectionType = () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenUpdateConnectionType(true);
  };

  // useEffect hooks
  useLayoutEffect(() => {
    if (permissionCodes[0] === STORAGE_KEY_ENUM.NON_INITIALIZED) return;

    if (!permissionCodes.includes('list-connection-type')) {
      navigate('/dashboard');
    }
  }, [permissionCodes, navigate]);

  useEffect(() => {
    if (!connectionTypes.length && !isEmptyConnectionTypes) {
      dispatch(connectionTypesActions.getConnectionTypes());
    }
  }, [connectionTypes, isEmptyConnectionTypes, dispatch]);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['create-connection-type']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenCreateConnectionType}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<AddIcon />}
              color="success"
              sx={{
                color: 'white',
                border: 'none',
                '&:hover': { backgroundColor: 'rgb(83, 189, 83, 0.8)', border: 'none', boxShadow: 'none' },
              }}>
              <FormattedMessage id="connectionTypes.createConnectionType" defaultMessage="Bağlantı Türü Oluştur" />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['update-connection-type']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenUpdateConnectionType}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<EditIcon />}
              color="secondary">
              <FormattedMessage id="connectionTypes.updateConnectionType" defaultMessage="Bağlantı Türü Düzenle" />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['delete-connection-type']}>
          <Grid size={2}>
            <Button
              onClick={handleClickOpenDeleteConnectionType}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<DeleteIcon />}
              color="error">
              <FormattedMessage id="connectionTypes.deleteConnectionType" defaultMessage="Bağlantı Türü Sil" />
            </Button>
          </Grid>
        </PermissionHandler>
      </Grid>
      <CreateConnectionTypeDialog
        open={openCreateConnectionType}
        handleClose={handleCloseCreateConnectionType}
        refresh={refresh}
      />
      <UpdateConnectionTypeDialog
        open={openUpdateConnectionType}
        connectionTypeId={rowSelectionModel[0]}
        handleClose={handleCloseUpdateConnectionType}
        refresh={refresh}
      />
      <DeleteDialog
        open={openDeleteConnectionType}
        id={rowSelectionModel[0]}
        handleClose={handleCloseDeleteConnectionType}
        refresh={refresh}
        setRowSelectionModel={setRowSelectionModel}
        title={<FormattedMessage id="connectionTypes.deleteConnectionType" defaultMessage="Bağlantı Türü Sil" />}
        subTitle={
          <FormattedMessage
            id="connectionTypes.deleteConnectionTypeRepeat"
            defaultMessage="Bu işlem onaylandıktan sonra bağlantı türü veritabanından silinecektir. Silme işlemini onaylıyor musunuz?"
          />
        }
        action={apiService.deleteConnectionType}
        successMessage={
          <FormattedMessage
            id="connectionTypes.deleteConnectionTypeCompleted"
            defaultMessage="Seçili bağlantı türünü silme işlemi başarıyla tamamlandı."
          />
        }
      />
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText()}
          rows={connectionTypes}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default ConnectionTypes;
